import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError, getTouringVocabulary, type TouringVocabIdentity } from "@/lib/api";
import { cn } from "@/lib/utils";
import type {
  DataHandlingPosture,
  HouseFeed,
  HouseFeedListResponse,
  Venue,
} from "@/lib/types";
import { DocumentsSection } from "@/components/DocumentsSection";
import { LocationSelect } from "@/components/LocationSelect";
import { useAuth } from "@/stores/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Common IANA timezones for the venue editor. Covers the North-American
// tour circuit plus a handful of others; the server validates any value
// against zoneinfo, so an unlisted-but-valid zone could still be set via
// the API. House-feed load-baselines plan KD10 / U10.
const COMMON_TIMEZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Phoenix",
  "America/Chicago",
  "America/New_York",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Vancouver",
  "America/Mexico_City",
  "Europe/London",
  "Europe/Paris",
  "Australia/Sydney",
] as const;

// Select of common IANA zones. The current value is always included even
// if it isn't in COMMON_TIMEZONES (a venue-doc extraction may have set an
// unlisted zone). Empty string = unset (cleared to NULL server-side).
function TimezoneSelect({
  value,
  onChange,
  id,
}: {
  value: string;
  onChange: (v: string) => void;
  id?: string;
}) {
  const options = value && !COMMON_TIMEZONES.includes(value as never)
    ? [value, ...COMMON_TIMEZONES]
    : [...COMMON_TIMEZONES];
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 w-full rounded border border-input bg-background px-2 text-sm"
    >
      <option value="">(not set)</option>
      {options.map((tz) => (
        <option key={tz} value={tz}>
          {tz}
        </option>
      ))}
    </select>
  );
}

export function AdminVenues() {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [openVenueId, setOpenVenueId] = useState<string | null>(() => {
    // U10's DiffReview's post-apply navigation drops an `openVenueId`
    // into localStorage so the operator lands back on the venue they
    // were applying against, with its row pre-expanded.
    const hint = localStorage.getItem("openVenueId");
    if (hint) localStorage.removeItem("openVenueId");
    return hint;
  });
  const navigate = useNavigate();
  const capabilities = useAuth((s) => s.capabilities);
  const posture: DataHandlingPosture | null = capabilities?.data_handling ?? null;

  const reload = () => {
    setLoading(true);
    api
      .get<{ venues: Venue[] }>("/api/venues")
      .then((r) => setVenues(r.venues))
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, []);

  const onDelete = async (venue_id: string) => {
    try {
      await api.delete(`/api/venues/${encodeURIComponent(venue_id)}`);
      reload();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        alert("Venue has active windows — close them first.");
      } else {
        alert(`Delete failed: ${e}`);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Venues</h1>
          <p className="text-sm text-muted-foreground">
            Physical locations + service-name templates used at load-in
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>New venue</Button>
          </DialogTrigger>
          <CreateVenueDialog onDone={() => { setCreateOpen(false); reload(); }} />
        </Dialog>
      </div>

      {error && <div className="text-destructive text-sm">{error}</div>}

      <Card>
        <CardHeader>
          <CardTitle>{venues.length} venue{venues.length === 1 ? "" : "s"}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : venues.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4">
              No venues yet. Create one to link events to physical locations.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Lat/Lon</TableHead>
                  <TableHead>Timezone</TableHead>
                  <TableHead>Templates</TableHead>
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {venues.map((v) => {
                  const isOpen = openVenueId === v.venue_id;
                  return (
                    <Fragment key={v.venue_id}>
                      <TableRow>
                        <TableCell className="font-medium">{v.name}</TableCell>
                        <TableCell>{v.address ?? "—"}</TableCell>
                        <TableCell className="tabular-nums text-xs">
                          {v.latitude?.toFixed(4) ?? "—"}, {v.longitude?.toFixed(4) ?? "—"}
                        </TableCell>
                        <TableCell className="text-xs">
                          <VenueTimezoneCell venue={v} onSaved={reload} />
                        </TableCell>
                        <TableCell className="text-xs">
                          {v.service_name_templates?.length ?? 0}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setOpenVenueId(isOpen ? null : v.venue_id)
                            }
                          >
                            {isOpen ? "Hide feeds" : "House feeds"}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDelete(v.venue_id)}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow>
                          <TableCell colSpan={6} className="bg-muted/30 p-3 space-y-3">
                            <DocumentsSection
                              venueId={v.venue_id}
                              posture={posture}
                              venueAckPosture={v.ai_processing_acknowledged_posture ?? null}
                              onOpenDiffReview={(documentId) =>
                                navigate(`/admin/venues/${v.venue_id}/diff/${documentId}`)
                              }
                            />
                            <HouseFeedCatalogEditor venue={v} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VenueTimezoneCell — inline view/edit of a venue's IANA timezone. The zone
// is the prerequisite for venue-local doors/curfew derivation on the Load-in
// tab (house-feed load-baselines plan KD10 / U10). Venue-document extraction
// can populate it automatically; this cell is the manual fallback / override.
// No optimistic state — wait for the PATCH response, then refetch the venue
// list (dyno-pwa convention; the service_location_label round-trip lesson).
// ---------------------------------------------------------------------------

function VenueTimezoneCell({
  venue,
  onSaved,
}: {
  venue: Venue;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(venue.timezone ?? "");
  const [saving, setSaving] = useState(false);

  // Re-sync the draft when the venue prop changes (after a refetch).
  useEffect(() => {
    setDraft(venue.timezone ?? "");
  }, [venue.timezone]);

  const save = async () => {
    setSaving(true);
    try {
      // Empty string clears the column to NULL server-side.
      await api.patch(`/api/venues/${encodeURIComponent(venue.venue_id)}`, {
        timezone: draft || null,
      });
      setEditing(false);
      onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) {
        const body = e.body as { error?: string };
        alert(`Couldn't save timezone: ${body?.error ?? "invalid timezone"}`);
      } else if (e instanceof ApiError && e.status === 403) {
        alert("Admin access required to change a venue timezone.");
      } else {
        alert(`Save failed: ${e}`);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-1">
        <span className={venue.timezone ? "" : "text-muted-foreground italic"}>
          {venue.timezone ?? "not set"}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-1.5 text-xs"
          onClick={() => {
            setDraft(venue.timezone ?? "");
            setEditing(true);
          }}
        >
          Edit
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <TimezoneSelect value={draft} onChange={setDraft} />
      <Button
        size="sm"
        className="h-6 px-2 text-xs"
        onClick={save}
        disabled={saving}
      >
        {saving ? "…" : "Save"}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-1.5 text-xs"
        onClick={() => {
          setDraft(venue.timezone ?? "");
          setEditing(false);
        }}
        disabled={saving}
      >
        Cancel
      </Button>
    </div>
  );
}


// ---------------------------------------------------------------------------
// House-feed catalog editor — inline per-venue. See
// docs/plans/2026-05-19-001-feat-house-feed-consolidation-plan.md (U7).
// Snapshot semantics on assignments mean catalog edits do NOT rewrite
// history; the audit view will surface divergence ("renamed" / "rerated")
// for events whose memberships were tagged before the edit.
// ---------------------------------------------------------------------------

// Aggregated catalog editor: groups feeds by (location_label, capacity_amps)
// and renders one row per bucket with a count + ± buttons. The editor moved
// from "name each feed individually" to "specify location + capacity +
// quantity" — operators rarely care about per-feed names, only how many
// feeds of each capacity exist at each location.

interface FeedBucket {
  key: string;
  location_label: string | null;
  capacity_amps: number;
  // Connector vocab from venue-doc plan KD9. Bucket-level because KD7
  // says one connector type per bucket (mixed types → separate buckets).
  connector_type: string | null;
  // Touring identities served by feeds in this bucket. Read from the
  // first feed — operators bucket-edit so all feeds in the bucket carry
  // the same value; a 'mixed' value is detected and surfaced in the row.
  served_identities: string[];
  served_identities_mixed: boolean;
  feeds: HouseFeed[];
}

// Build a display summary for a bucket's notes column.
//   - empty across all feeds → "—"
//   - all feeds carry the same note → that note (truncated for the cell)
//   - distinct notes → "; "-joined (truncated). Full text returned as
//     `tooltip` so the cell can show it on hover.
function bucketNotesSummary(feeds: HouseFeed[]): { display: string; tooltip: string } {
  const notes = feeds
    .map((f) => (f.notes || "").trim())
    .filter((n) => n.length > 0);
  if (notes.length === 0) return { display: "—", tooltip: "" };
  const unique = Array.from(new Set(notes));
  const full = unique.length === 1 ? unique[0] : unique.join("; ");
  const display = full.length > 60 ? full.slice(0, 60) + "…" : full;
  return { display, tooltip: full };
}

function bucketize(feeds: HouseFeed[]): FeedBucket[] {
  const map = new Map<string, FeedBucket>();
  for (const f of feeds) {
    const loc = f.location_label ?? "";
    const conn = f.connector_type ?? "";
    // Match key includes connector per KD7 / KD16 — feeds with different
    // connector types are different physical buckets even at the same
    // location + capacity.
    const key = `${loc}|${f.capacity_amps}|${conn}`;
    const existing = map.get(key);
    if (existing) {
      existing.feeds.push(f);
      // Detect served_identities divergence across feeds in the bucket so
      // the row can warn the operator.
      const a = JSON.stringify([...existing.served_identities].sort());
      const b = JSON.stringify([...(f.served_identities ?? [])].sort());
      if (a !== b) existing.served_identities_mixed = true;
    } else {
      map.set(key, {
        key,
        location_label: f.location_label,
        capacity_amps: f.capacity_amps,
        connector_type: f.connector_type ?? null,
        served_identities: f.served_identities ?? [],
        served_identities_mixed: false,
        feeds: [f],
      });
    }
  }
  // Sort by location name, then capacity ascending, then connector.
  return Array.from(map.values()).sort((a, b) => {
    const la = (a.location_label ?? "~").toLowerCase();
    const lb = (b.location_label ?? "~").toLowerCase();
    if (la !== lb) return la.localeCompare(lb);
    if (a.capacity_amps !== b.capacity_amps)
      return a.capacity_amps - b.capacity_amps;
    return (a.connector_type ?? "").localeCompare(b.connector_type ?? "");
  });
}

function HouseFeedCatalogEditor({ venue }: { venue: Venue }) {
  const [feeds, setFeeds] = useState<HouseFeed[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newAmps, setNewAmps] = useState("");
  const [newLocation, setNewLocation] = useState("");
  const [newQuantity, setNewQuantity] = useState("1");
  const [creating, setCreating] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const reload = () => {
    setLoading(true);
    setError(null);
    api
      .get<HouseFeedListResponse>(
        `/api/venues/${encodeURIComponent(venue.venue_id)}/house-feeds`
      )
      .then((r) => setFeeds(r.feeds))
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue.venue_id]);

  const buckets = bucketize(feeds);

  const addBucket = async () => {
    const ampsN = Number.parseInt(newAmps, 10);
    if (!Number.isFinite(ampsN) || ampsN <= 0 || ampsN > 6000) {
      alert("Capacity must be between 1 and 6000 amps.");
      return;
    }
    const qtyN = Number.parseInt(newQuantity, 10);
    if (!Number.isFinite(qtyN) || qtyN < 1 || qtyN > 50) {
      alert("Quantity must be between 1 and 50.");
      return;
    }
    setCreating(true);
    try {
      const resp = await api.post<HouseFeed | { created: HouseFeed[] }>(
        `/api/venues/${encodeURIComponent(venue.venue_id)}/house-feeds`,
        {
          capacity_amps: ampsN,
          location_label: newLocation.trim() || null,
          quantity: qtyN,
        }
      );
      const created: HouseFeed[] = Array.isArray(
        (resp as { created?: HouseFeed[] }).created,
      )
        ? (resp as { created: HouseFeed[] }).created
        : [resp as HouseFeed];
      setFeeds((prev) => [...prev, ...created]);
      setNewAmps("");
      setNewLocation("");
      setNewQuantity("1");
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) {
        const body = e.body as { error?: string };
        alert(`Couldn't add feeds: ${body?.error ?? "validation failed"}`);
      } else {
        alert(`Add failed: ${e}`);
      }
    } finally {
      setCreating(false);
    }
  };

  const bumpBucket = async (bucket: FeedBucket) => {
    setBusyKey(bucket.key);
    try {
      const created = await api.post<HouseFeed>(
        `/api/venues/${encodeURIComponent(venue.venue_id)}/house-feeds`,
        {
          capacity_amps: bucket.capacity_amps,
          location_label: bucket.location_label,
          quantity: 1,
        },
      );
      setFeeds((prev) => [...prev, created]);
    } catch (e) {
      alert(`Couldn't add one more: ${e}`);
    } finally {
      setBusyKey(null);
    }
  };

  const removeOneFromBucket = async (bucket: FeedBucket) => {
    // Pop the most-recently-created feed in the bucket. If it has an
    // active assignment the API returns 409 and we surface the message.
    const victim = bucket.feeds
      .slice()
      .sort((a, b) =>
        (b.created_at ?? "").localeCompare(a.created_at ?? ""),
      )[0];
    if (!victim) return;
    setBusyKey(bucket.key);
    try {
      await api.delete(
        `/api/venues/${encodeURIComponent(venue.venue_id)}/house-feeds/${encodeURIComponent(victim.feed_id)}`,
      );
      setFeeds((prev) => prev.filter((f) => f.feed_id !== victim.feed_id));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        alert(
          "The most-recent feed in this bucket has an active assignment. "
          + "Clear the assignment in Load-in first, or remove a different feed.",
        );
      } else {
        alert(`Couldn't remove one: ${e}`);
      }
    } finally {
      setBusyKey(null);
    }
  };

  const removeWholeBucket = async (bucket: FeedBucket) => {
    if (
      !confirm(
        `Delete all ${bucket.feeds.length} feeds in this bucket `
        + `(${bucket.location_label ?? "no location"} · ${bucket.capacity_amps}A)?`,
      )
    ) {
      return;
    }
    setBusyKey(bucket.key);
    const failures: string[] = [];
    for (const f of bucket.feeds) {
      try {
        await api.delete(
          `/api/venues/${encodeURIComponent(venue.venue_id)}/house-feeds/${encodeURIComponent(f.feed_id)}`,
        );
        setFeeds((prev) => prev.filter((x) => x.feed_id !== f.feed_id));
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          failures.push("one or more feeds in this bucket have active assignments");
          break;
        }
        failures.push(String(e));
      }
    }
    if (failures.length > 0) {
      alert(`Couldn't fully delete bucket: ${failures.join("; ")}`);
    }
    setBusyKey(null);
  };

  // Bulk edit: apply the same patch to every feed in a bucket. Used by
  // EditBucketDialog. Lets operators correct AI extraction mistakes
  // (capacity mislabeled as 200A when it's 250A, connector wrong, etc.)
  // without going through a full re-analyze cycle.
  const updateBucket = async (
    bucket: FeedBucket,
    patch: Partial<Pick<HouseFeed, "location_label" | "capacity_amps" | "connector_type" | "notes" | "served_identities">>,
  ) => {
    setBusyKey(bucket.key);
    try {
      for (const f of bucket.feeds) {
        const updated = await api.patch<HouseFeed>(
          `/api/venues/${encodeURIComponent(venue.venue_id)}/house-feeds/${encodeURIComponent(f.feed_id)}`,
          patch,
        );
        setFeeds((prev) =>
          prev.map((x) => (x.feed_id === f.feed_id ? updated : x)),
        );
      }
    } catch (e) {
      alert(`Edit failed: ${e instanceof ApiError ? (e.body as { error?: string })?.error ?? e.message : String(e)}`);
    } finally {
      setBusyKey(null);
    }
  };

  const [editBucket, setEditBucket] = useState<FeedBucket | null>(null);

  return (
    <div className="space-y-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        House feeds for {venue.name}
      </div>
      {error && <div className="text-destructive text-sm">{error}</div>}
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Location</TableHead>
              <TableHead className="w-24">Capacity</TableHead>
              <TableHead className="w-32">Connector</TableHead>
              <TableHead className="w-48">Identities served</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead className="w-32 text-center">Quantity</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {buckets.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-sm text-muted-foreground py-2">
                  No feeds yet — pick a location, capacity, and quantity below.
                </TableCell>
              </TableRow>
            )}
            {buckets.map((b) => {
              const notes = bucketNotesSummary(b.feeds);
              return (
                <TableRow key={b.key}>
                  <TableCell>{b.location_label ?? <span className="text-muted-foreground italic">unspecified</span>}</TableCell>
                  <TableCell>{b.capacity_amps}A</TableCell>
                  <TableCell>
                    {b.connector_type
                      ? <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium">{b.connector_type}</span>
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {b.served_identities.length === 0 ? (
                      <span className="text-muted-foreground italic">none</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {b.served_identities.map((ident) => (
                          <span
                            key={ident}
                            className="rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 px-1.5 py-0.5 text-[10px] font-medium"
                          >
                            {ident}
                          </span>
                        ))}
                        {b.served_identities_mixed && (
                          <span
                            className="text-amber-400"
                            title="Feeds in this bucket carry different served_identities. Edit applies a single value to all."
                          >
                            (mixed)
                          </span>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground" title={notes.tooltip || undefined}>
                    {notes.display}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={() => removeOneFromBucket(b)}
                        disabled={busyKey === b.key || b.feeds.length === 0}
                        title="Remove one feed from this bucket"
                      >
                        −
                      </Button>
                      <span className="tabular-nums text-sm font-medium min-w-[2ch] text-center">
                        {b.feeds.length}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={() => bumpBucket(b)}
                        disabled={busyKey === b.key}
                        title="Add one more feed to this bucket"
                      >
                        +
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditBucket(b)}
                        disabled={busyKey === b.key}
                        title="Edit location / capacity / connector / notes for every feed in this bucket"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeWholeBucket(b)}
                        disabled={busyKey === b.key}
                      >
                        Delete all
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {/* Inline add row — connector + notes columns left blank;
                the manual add flow leaves them null (operator can edit
                later via re-upload or, in v1.1, inline editing). */}
            <TableRow>
              <TableCell>
                <LocationSelect
                  value={newLocation}
                  onChange={setNewLocation}
                />
              </TableCell>
              <TableCell>
                <Input
                  value={newAmps}
                  onChange={(e) => setNewAmps(e.target.value)}
                  placeholder="Amps (required)"
                  inputMode="numeric"
                  className="h-8"
                />
              </TableCell>
              <TableCell>
                <span className="text-xs text-muted-foreground italic">
                  set via doc extraction
                </span>
              </TableCell>
              <TableCell>
                <span className="text-xs text-muted-foreground italic">
                  edit after create
                </span>
              </TableCell>
              <TableCell>
                <span className="text-xs text-muted-foreground italic">
                  set via doc extraction
                </span>
              </TableCell>
              <TableCell>
                <Input
                  value={newQuantity}
                  onChange={(e) => setNewQuantity(e.target.value)}
                  placeholder="1"
                  inputMode="numeric"
                  className="h-8 text-center"
                />
              </TableCell>
              <TableCell className="text-right">
                <Button
                  size="sm"
                  onClick={addBucket}
                  disabled={creating}
                  title={
                    !newAmps.trim()
                      ? "Enter capacity (1–6000 amps) first"
                      : !newQuantity.trim()
                        ? "Enter quantity (1–50)"
                        : "Add feeds"
                  }
                >
                  {creating ? "…" : "Add"}
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}
      <EditBucketDialog
        bucket={editBucket}
        onCancel={() => setEditBucket(null)}
        onSave={async (patch) => {
          if (editBucket) await updateBucket(editBucket, patch);
          setEditBucket(null);
        }}
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// EditBucketDialog — bulk-edit every feed in a (location, capacity, connector)
// bucket. The catalog row's "Edit" button mounts this dialog. Common use case:
// AI extraction got capacity_amps slightly wrong (200A vs verified 250A) and
// the operator needs to fix all N feeds in the bucket in one shot.
// ---------------------------------------------------------------------------

function EditBucketDialog({
  bucket,
  onCancel,
  onSave,
}: {
  bucket: FeedBucket | null;
  onCancel: () => void;
  onSave: (patch: Partial<Pick<HouseFeed, "location_label" | "capacity_amps" | "connector_type" | "notes" | "served_identities">>) => Promise<void>;
}) {
  const [location, setLocation] = useState("");
  const [capacity, setCapacity] = useState("");
  const [connector, setConnector] = useState("");
  const [customConnector, setCustomConnector] = useState(false);
  const [notes, setNotes] = useState("");
  const [identities, setIdentities] = useState<string[]>([]);
  const [vocab, setVocab] = useState<TouringVocabIdentity[]>([]);
  const [saving, setSaving] = useState(false);

  // Reset form when bucket changes.
  useEffect(() => {
    if (!bucket) return;
    setLocation(bucket.location_label ?? "");
    setCapacity(String(bucket.capacity_amps));
    const c = bucket.connector_type ?? "";
    const connectorVocab = ["Cam-Lock", "Hubbell L2130", "bare-wire-to-lugs"];
    setConnector(c);
    setCustomConnector(!!c && !connectorVocab.includes(c));
    // Notes column in the bucket is a summary; for the dialog we show
    // the first feed's notes (or empty) — bulk-edit applies the new
    // value to every feed regardless of prior divergence.
    setNotes(bucket.feeds[0]?.notes ?? "");
    setIdentities(bucket.served_identities ?? []);
  }, [bucket]);

  // Pull the touring vocabulary (canonicalized union across all tours)
  // for the multi-select. Refetched per-mount; rare enough that we don't
  // need a global cache here.
  useEffect(() => {
    if (!bucket) return;
    getTouringVocabulary()
      .then((r) => setVocab(r.identities))
      .catch(() => setVocab([]));
  }, [bucket]);

  if (!bucket) return null;

  function toggleIdentity(label: string) {
    setIdentities((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  }

  const handleSave = async () => {
    const ampsN = Number.parseInt(capacity, 10);
    if (!Number.isFinite(ampsN) || ampsN <= 0 || ampsN > 6000) {
      alert("Capacity must be 1–6000 amps.");
      return;
    }
    if (notes.length > 280) {
      alert("Notes must be ≤ 280 characters.");
      return;
    }
    setSaving(true);
    const trimmedLoc = location.trim();
    const trimmedConn = connector.trim();
    await onSave({
      location_label: trimmedLoc || null,
      capacity_amps: ampsN,
      connector_type: trimmedConn || null,
      notes: notes.trim() || null,
      served_identities: identities,
    });
    setSaving(false);
  };

  return (
    <Dialog open={!!bucket} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Edit bucket: {bucket.feeds.length} feed
            {bucket.feeds.length === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Changes apply to every feed in this bucket. Past event assignments
            keep their original snapshot — only the current catalog row updates.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Location</Label>
            <LocationSelect value={location} onChange={setLocation} />
          </div>
          <div>
            <Label className="text-xs">Capacity (amps)</Label>
            <Input
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              inputMode="numeric"
              className="h-8"
            />
          </div>
          <div>
            <Label className="text-xs">Connector type</Label>
            <select
              value={customConnector ? "__custom__" : connector}
              onChange={(e) => {
                if (e.target.value === "__custom__") {
                  setCustomConnector(true);
                  setConnector("");
                } else {
                  setCustomConnector(false);
                  setConnector(e.target.value);
                }
              }}
              className="h-8 w-full rounded border border-input bg-background px-2 text-sm"
            >
              <option value="">(none)</option>
              <option value="Cam-Lock">Cam-Lock</option>
              <option value="Hubbell L2130">Hubbell L2130</option>
              <option value="bare-wire-to-lugs">bare-wire-to-lugs</option>
              <option value="__custom__">Other…</option>
            </select>
            {customConnector && (
              <Input
                value={connector}
                onChange={(e) => setConnector(e.target.value)}
                placeholder="Custom connector"
                className="mt-1 h-8"
              />
            )}
          </div>
          <div>
            <Label className="text-xs">Touring identities served</Label>
            <p className="text-[11px] text-muted-foreground mb-1">
              Which touring requirements does this feed land on at this venue?
              Pick from the normalized union of all tours' vocabularies.
              Matches partition.touring_identity on the suggestions planner.
            </p>
            {vocab.length === 0 ? (
              <p className="text-xs text-muted-foreground italic py-1">
                No touring vocabulary yet — add identities by editing a tour's
                vocabulary, then come back.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {vocab.map((v) => {
                  const active = identities.includes(v.label);
                  return (
                    <button
                      key={v.label}
                      type="button"
                      onClick={() => toggleIdentity(v.label)}
                      className={cn(
                        "rounded border px-2 py-0.5 text-xs transition-colors",
                        active
                          ? "bg-emerald-500/20 text-emerald-200 border-emerald-500/40"
                          : "bg-muted text-muted-foreground border-border hover:text-foreground",
                      )}
                      title={`${v.label} · ${v.tier}-side`}
                    >
                      {v.label}
                      <span className="ml-1 text-[9px] text-muted-foreground/70">
                        {v.tier}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {bucket.served_identities_mixed && (
              <p className="text-[11px] text-amber-400 mt-1">
                Feeds in this bucket currently carry different values. Save
                applies this single list to every feed in the bucket.
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs">Notes (≤ 280 chars)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={280}
              className="text-sm"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving
              ? "Saving…"
              : `Apply to ${bucket.feeds.length} feed${bucket.feeds.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function CreateVenueDialog({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [timezone, setTimezone] = useState("");
  const [templates, setTemplates] = useState("[]");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setErr(null);
    setSaving(true);
    try {
      const body: Record<string, unknown> = { name };
      if (address) body.address = address;
      if (lat) body.latitude = parseFloat(lat);
      if (lon) body.longitude = parseFloat(lon);
      if (timezone) body.timezone = timezone;
      try {
        body.service_name_templates = JSON.parse(templates);
      } catch {
        throw new Error("service_name_templates must be valid JSON array");
      }
      await api.post("/api/venues", body);
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>New venue</DialogTitle>
        <DialogDescription>
          Address + GPS are optional. Templates are JSON list shape.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="v-name">Name</Label>
          <Input id="v-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="v-addr">Address</Label>
          <Input id="v-addr" value={address} onChange={(e) => setAddress(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label htmlFor="v-lat">Latitude</Label>
            <Input id="v-lat" value={lat} onChange={(e) => setLat(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="v-lon">Longitude</Label>
            <Input id="v-lon" value={lon} onChange={(e) => setLon(e.target.value)} />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="v-tz">Timezone</Label>
          <TimezoneSelect id="v-tz" value={timezone} onChange={setTimezone} />
          <p className="text-xs text-muted-foreground">
            IANA zone for venue-local doors/curfew. Can also be set later or
            populated from an uploaded venue document.
          </p>
        </div>
        <div className="space-y-1">
          <Label htmlFor="v-tpl">Service-name templates (JSON)</Label>
          <Textarea
            id="v-tpl"
            value={templates}
            rows={4}
            onChange={(e) => setTemplates(e.target.value)}
          />
        </div>
        {err && <div className="text-destructive text-sm">{err}</div>}
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={!name || saving}>
          {saving ? "Saving…" : "Create"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

